import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FaNetworkWired, FaEnvelope, FaUserCircle, FaChartLine, FaTools, FaCalculator, FaBookOpen,
  FaSpinner, FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaRedo,
} from 'react-icons/fa';
import { Modal } from './common';
import axiosInstance from '../utils/axiosConfig';
import { formatFecha } from './vacantesShared';
import './VacantesUI.css';
import './DeleteEmployeeModal.css';

// Lo que se puede hacer con cada cuenta de quien se va (la primera es la de
// siempre; "Dejar" no la toca). Centro Sura no tiene opciones: el egreso ya la
// desactiva. CIMA y Profit guardan historia de la persona: ahí se desactiva y
// se bloquea, no se borra. En CIMA además se vacía su silla.
const SISTEMAS = [
  { clave: 'red', nombre: 'Red', icono: FaNetworkWired,
    acciones: [{ valor: 'deshabilitar', texto: 'Deshabilitar' }] },
  { clave: 'correo', nombre: 'Correo', icono: FaEnvelope,
    acciones: [
      { valor: 'redirigir', texto: 'Redirigir' },
      { valor: 'suspender', texto: 'Suspender' },
      { valor: 'borrar', texto: 'Borrar', peligro: true },
    ] },
  { clave: 'centro_sura', nombre: 'Centro Sura', icono: FaUserCircle, acciones: [] },
  { clave: 'cima', nombre: 'CIMA', icono: FaChartLine,
    acciones: [{ valor: 'egresar', texto: 'Cerrar' }] },
  { clave: 'soluciones_ti', nombre: 'Soluciones TI', icono: FaTools,
    acciones: [{ valor: 'borrar', texto: 'Borrar', peligro: true }] },
  { clave: 'profit', nombre: 'Profit', icono: FaCalculator,
    acciones: [{ valor: 'bloquear', texto: 'Bloquear' }] },
  { clave: 'catalogo', nombre: 'Catálogo', icono: FaBookOpen,
    acciones: [{ valor: 'desactivar', texto: 'Desactivar' }] },
];

// En un movimiento interno solo se vacía su silla de CIMA: sigue trabajando
const SISTEMAS_MOVIMIENTO = [
  { ...SISTEMAS[3], acciones: [{ valor: 'egresar', texto: 'Vaciar silla' }] },
];

const DEJAR = { valor: 'dejar', texto: 'Dejar' };

const EXPLICACION_CORREO = {
  redirigir: 'Lo que le escriban le llega a esa persona. Su clave cambia por una nueva, se cierran sus sesiones y le enviamos la clave por correo.',
  suspender: 'No puede entrar ni recibe correos nuevos. Su buzón y su Drive se conservan.',
  borrar: 'Se pierden su buzón y su Drive. Google deja restaurarla durante 20 días.',
};

const NADIE = 'nadie';

const clientes = (n) => (n === 1 ? '1 cliente' : `${n || 0} clientes`);

/** El estado de CIMA viene de su consulta de egreso, no del panel Cuentas:
 *  trae su silla, las que atiende y quiénes pueden quedarse con ellas. */
const hayAlgoEnCima = (d, tipo) =>
  !!d && (!!d.silla || (d.suplencias || []).length > 0 || (tipo === 'egreso' && !!d.usuario?.isActive));

/**
 * Las cuentas de quien se va, una fila por sistema: qué tiene, qué se hace con
 * cada una y, después de aplicar, cómo le fue a cada una. Cada sistema se pide
 * y se aplica aparte: uno lento o caído no tranca a los demás.
 */
export function useCierreCuentas(empleadoId, { habilitado = true, tipo = 'egreso' } = {}) {
  const sistemas = tipo === 'movimiento' ? SISTEMAS_MOVIMIENTO : SISTEMAS;
  const [cuentas, setCuentas] = useState({}); // clave -> { cargando, datos, error }
  const [elegidas, setElegidas] = useState({}); // clave -> acción (si TI cambió la de siempre)
  const [destinos, setDestinos] = useState(null); // { sugerido, jefe, opciones }
  const [destino, setDestino] = useState('');
  const [suplentes, setSuplentes] = useState({}); // código de silla -> empleadoId | 'nadie'
  const [aplicadas, setAplicadas] = useState({}); // clave -> acción confirmada
  const [resultados, setResultados] = useState({}); // clave -> { estado, mensaje, clave, alerta }

  const consultar = useCallback(async (sistema) => {
    setCuentas((c) => ({ ...c, [sistema]: { cargando: true } }));
    const url = sistema === 'cima'
      ? `/api/empleados/${empleadoId}/egreso/cima`
      : `/api/empleados/${empleadoId}/cuentas/${sistema}`;
    try {
      const r = await axiosInstance.get(url);
      setCuentas((c) => ({ ...c, [sistema]: { cargando: false, datos: r.data } }));
    } catch (err) {
      setCuentas((c) => ({
        ...c,
        [sistema]: { cargando: false, error: err.response?.data?.error || 'No se pudo consultar' },
      }));
    }
  }, [empleadoId]);

  useEffect(() => {
    if (!habilitado) return;
    sistemas.forEach((s) => consultar(s.clave));
    if (tipo === 'movimiento') return;
    axiosInstance.get(`/api/empleados/${empleadoId}/egreso/destinos-correo`)
      .then((r) => {
        setDestinos(r.data);
        setDestino((actual) => actual || r.data.sugerido || '');
      })
      .catch(() => setDestinos({ sugerido: null, jefe: null, opciones: [] }));
    // sistemas depende solo de tipo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habilitado, tipo, consultar, empleadoId]);

  // Una cuenta se puede tocar si existe, sigue activa y (el correo) es de Google
  const gestionable = (s) => {
    const d = cuentas[s.clave]?.datos;
    if (s.clave === 'cima') return hayAlgoEnCima(d, tipo);
    if (!d?.existe || d.activa === false || s.acciones.length === 0) return false;
    if (s.clave === 'correo') return (d.usuario || '').endsWith('@sura.com.ve');
    return true;
  };

  const accionDe = (s) => {
    if (!gestionable(s)) return null;
    if (elegidas[s.clave]) return elegidas[s.clave];
    // El correo se redirige al jefe cuando tiene cuenta de Google; si no, se suspende
    if (s.clave === 'correo') return destinos?.sugerido ? 'redirigir' : 'suspender';
    return s.acciones[0].valor;
  };

  // Quién atiende cada silla: lo que eligió TI o, de entrada, el más cercano arriba
  const suplenteDe = (codigo, candidatos) => {
    if (suplentes[codigo] !== undefined) return suplentes[codigo];
    return candidatos?.length ? String(candidatos[0].empleadoId) : NADIE;
  };

  const correo = sistemas.find((s) => s.clave === 'correo');
  const faltaDestino = !!correo && accionDe(correo) === 'redirigir' && !destino;
  const consultando = habilitado && sistemas.some((s) => !cuentas[s.clave] || cuentas[s.clave].cargando);

  const aplicar = async (clave, accion) => {
    setResultados((r) => ({ ...r, [clave]: { estado: 'trabajando' } }));
    const cuerpo = { accion };
    if (clave === 'correo') cuerpo.destino = destino;
    if (clave === 'cima') {
      const d = cuentas.cima?.datos || {};
      const id = (codigo, candidatos) => {
        const v = suplenteDe(codigo, candidatos);
        return v === NADIE ? null : Number(v);
      };
      cuerpo.tipo = tipo;
      cuerpo.suplente_empleado_id = d.silla?.esVenta ? id(d.silla.codigo, d.silla.candidatos) : null;
      cuerpo.suplencias = (d.suplencias || []).map((x) => ({
        silla: x.sillaCodigo, suplente_empleado_id: id(x.sillaCodigo, x.candidatos),
      }));
    }
    let resultado;
    try {
      const r = await axiosInstance.post(`/api/empleados/${empleadoId}/egreso/cuentas/${clave}`, cuerpo);
      resultado = { estado: 'ok', ...r.data };
    } catch (err) {
      resultado = { estado: 'error', mensaje: err.response?.data?.error || 'No se pudo completar' };
    }
    setResultados((x) => ({ ...x, [clave]: resultado }));
    return resultado;
  };

  /** Aplica lo elegido en todas las cuentas a la vez. Devuelve qué pasó, para
   *  los avisos del egreso: `cuentas` en palabras ([{ sistema, resultado }],
   *  para TI) y `paraJefe` ({ correo, cima }: lo que se le cuenta a su jefe). */
  const aplicarTodo = async () => {
    const porAplicar = {};
    sistemas.forEach((s) => {
      const accion = accionDe(s);
      if (accion && accion !== 'dejar') porAplicar[s.clave] = accion;
    });
    setAplicadas(porAplicar);
    const hechos = await Promise.all(Object.entries(porAplicar).map(([clave, accion]) => aplicar(clave, accion)));
    const porClave = Object.fromEntries(Object.keys(porAplicar).map((clave, i) => [clave, hechos[i]]));

    const sCorreo = sistemas.find((s) => s.clave === 'correo');
    const accionCorreo = sCorreo ? accionDe(sCorreo) : null;
    const correoJefe = accionCorreo
      ? { accion: accionCorreo, destino, ok: accionCorreo === 'dejar' || porClave.correo?.estado === 'ok' }
      : null;
    const dCima = cuentas.cima?.datos;
    let cimaJefe = null;
    if (porClave.cima?.estado === 'ok' && dCima?.silla) {
      const id = suplenteDe(dCima.silla.codigo, dCima.silla.candidatos);
      const elegido = (dCima.silla.candidatos || []).find((c) => String(c.empleadoId) === id);
      cimaJefe = { silla: dCima.silla.codigo, suplente: dCima.silla.esVenta ? elegido?.nombre || null : null };
    }

    const lista = sistemas.map((s) => {
      const r = porClave[s.clave];
      if (r) return { sistema: s.nombre, resultado: r.estado === 'ok' ? r.mensaje : `No se pudo: ${r.mensaje}` };
      const d = cuentas[s.clave]?.datos;
      if (s.clave === 'centro_sura' && d?.existe) return { sistema: s.nombre, resultado: `Usuario ${d.usuario} desactivado` };
      if (cuentas[s.clave]?.error) return { sistema: s.nombre, resultado: 'No se pudo consultar: queda pendiente' };
      const tiene = s.clave === 'cima' ? hayAlgoEnCima(d, tipo) : d?.existe && d.activa !== false;
      return tiene ? { sistema: s.nombre, resultado: 'Se dejó como estaba' } : null;
    }).filter(Boolean);
    return { cuentas: lista, paraJefe: { correo: correoJefe, cima: cimaJefe } };
  };

  return {
    tipo, sistemas, cuentas, consultar, accionDe, setElegidas, destinos, destino, setDestino,
    suplenteDe, setSuplentes, faltaDestino, consultando, aplicadas, resultados, aplicar, aplicarTodo,
  };
}

function SelectorSuplente({ id, codigo, candidatos, cierre }) {
  return (
    <select
      id={id}
      className="input"
      value={cierre.suplenteDe(codigo, candidatos)}
      onChange={(e) => cierre.setSuplentes((x) => ({ ...x, [codigo]: e.target.value }))}
    >
      {(candidatos || []).map((c) => (
        <option key={c.empleadoId} value={String(c.empleadoId)}>
          {c.nombre} ({c.sillaNombre})
        </option>
      ))}
      <option value={NADIE}>Nadie: su cartera queda sin atender</option>
    </select>
  );
}

function DetalleCima({ d, accion, cierre }) {
  if (!accion || accion === 'dejar') return null;
  const { silla } = d;
  return (
    <div className="egr-extra">
      {silla && silla.esVenta && (
        <div className="egr-destino">
          <label htmlFor="cima-suplente">Su cartera la atiende</label>
          <SelectorSuplente id="cima-suplente" codigo={silla.codigo} candidatos={silla.candidatos} cierre={cierre} />
        </div>
      )}
      {silla && !silla.esVenta && (
        <p className="m-0 mb-1">
          Su silla de mando queda vacía. Las sillas que dependen de ella siguen en su lugar hasta que llegue su reemplazo.
        </p>
      )}
      {(d.suplencias || []).length > 0 && (
        <div className="mt-2">
          <p className="m-0 mb-1 text-gray-700 font-medium">Sillas vacías que atendía</p>
          {d.suplencias.map((x) => (
            <div key={x.sillaCodigo} className="egr-destino">
              <label htmlFor={`cima-sup-${x.sillaCodigo}`}>{x.sillaNombre} ({clientes(x.clientesCartera)})</label>
              <SelectorSuplente id={`cima-sup-${x.sillaCodigo}`} codigo={x.sillaCodigo} candidatos={x.candidatos} cierre={cierre} />
            </div>
          ))}
        </div>
      )}
      {cierre.tipo === 'egreso' && d.usuario?.isActive && (
        <p className="m-0 mt-1">Su usuario se desactiva y se le cierran las sesiones, también en el teléfono.</p>
      )}
    </div>
  );
}

/** Las filas para elegir qué pasa con cada cuenta. */
export function ListaCuentas({ cierre }) {
  const fila = (s) => {
    const estado = cierre.cuentas[s.clave] || { cargando: true };
    const d = estado.datos;
    const Icono = s.icono;
    const accion = cierre.accionDe(s);
    const esCima = s.clave === 'cima';
    const existe = esCima ? !!(d?.usuario || d?.silla || d?.suplencias?.length) : d?.existe;
    const apagada = !estado.cargando && !estado.error && !accion && s.clave !== 'centro_sura';

    let usuario = d?.usuario;
    if (esCima) {
      usuario = [d?.usuario?.username, d?.silla && `silla ${d.silla.codigo} (${clientes(d.silla.clientesCartera)})`]
        .filter(Boolean).join(' · ');
    }

    let derecha;
    if (estado.cargando) {
      derecha = <span className="egr-cargando" aria-label={`Consultando ${s.nombre}`} />;
    } else if (estado.error) {
      derecha = (
        <span className="egr-nota flex items-center gap-2 sm:justify-end">
          <span className="text-red-700">{estado.error}</span>
          <button type="button" className="vac-link-btn inline-flex items-center gap-1" onClick={() => cierre.consultar(s.clave)}>
            <FaRedo aria-hidden="true" /> Reintentar
          </button>
        </span>
      );
    } else if (!existe) {
      derecha = <span className="egr-nota">No tiene</span>;
    } else if (s.clave === 'centro_sura') {
      derecha = <span className="egr-nota">{d.activa === false ? 'Ya está desactivado' : 'Se desactiva con el egreso'}</span>;
    } else if (!accion) {
      derecha = (
        <span className="egr-nota">
          {s.clave === 'correo' && d.activa !== false ? 'Cuenta de cPanel: se cierra desde el hosting' : 'Ya está cerrada'}
        </span>
      );
    } else {
      derecha = (
        <span className="egr-opciones" role="radiogroup" aria-label={`Qué pasa con su cuenta de ${s.nombre}`}>
          {[...s.acciones, DEJAR].map((op) => (
            <button
              key={op.valor}
              type="button"
              role="radio"
              aria-checked={accion === op.valor}
              className={`egr-opcion${accion === op.valor ? ' egr-opcion--activa' : ''}${op.peligro ? ' egr-opcion--peligro' : ''}`}
              onClick={() => cierre.setElegidas((e) => ({ ...e, [s.clave]: op.valor }))}
            >
              {op.texto}
            </button>
          ))}
        </span>
      );
    }

    return (
      <li key={s.clave} className={`egr-fila${apagada ? ' egr-fila--apagada' : ''}`}>
        <div className="egr-linea">
          <span className="egr-sistema">
            <span className="egr-icono"><Icono aria-hidden="true" /></span>
            <span className="min-w-0">
              <span className="egr-nombre">{s.nombre}</span>
              {usuario && <span className="egr-usuario">{usuario}</span>}
            </span>
          </span>
          {derecha}
        </div>

        {s.clave === 'correo' && accion && accion !== 'dejar' && (
          <div className={`egr-extra${accion === 'borrar' ? ' egr-extra--peligro' : ''}`}>
            {accion === 'redirigir' && (
              <div className="egr-destino">
                <label htmlFor="egreso-destino">Redirigir a</label>
                <select
                  id="egreso-destino"
                  className="input"
                  value={cierre.destino}
                  onChange={(e) => cierre.setDestino(e.target.value)}
                >
                  <option value="">Elige una persona</option>
                  {(cierre.destinos?.opciones || []).map((o) => (
                    <option key={o.id} value={o.correo}>
                      {o.nombre}{o.correo === cierre.destinos.sugerido ? ' (su jefe)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <p className="m-0">{EXPLICACION_CORREO[accion]}</p>
          </div>
        )}

        {esCima && d && <DetalleCima d={d} accion={accion} cierre={cierre} />}

        {esCima && accion && accion !== 'dejar' && (d?.avisos || []).map((aviso) => (
          <p key={aviso} className="egr-aviso"><FaExclamationTriangle aria-hidden="true" /> {aviso}</p>
        ))}
      </li>
    );
  };

  return <ul className="egr-cuentas list-none m-0 p-0">{cierre.sistemas.map(fila)}</ul>;
}

/** Las filas con el resultado de lo aplicado en cada cuenta. */
export function ResultadosCuentas({ cierre, egresoRegistrado = true }) {
  const fila = (s) => {
    const estado = cierre.cuentas[s.clave];
    const d = estado?.datos;
    const accion = cierre.aplicadas[s.clave];
    const r = cierre.resultados[s.clave];
    const existe = s.clave === 'cima' ? hayAlgoEnCima(d, cierre.tipo) : d?.existe;
    if (!existe && !accion && !estado?.error) return null;
    const Icono = s.icono;

    let derecha;
    if (estado?.error && !accion) {
      // No se pudo ver qué tenía: queda para después, desde su expediente
      derecha = (
        <span className="egr-resultado egr-resultado--alerta">
          <FaExclamationTriangle aria-hidden="true" /> No se pudo consultar. Ciérrala luego con Cerrar cuentas, en su expediente
        </span>
      );
    } else if (s.clave === 'centro_sura') {
      if (!egresoRegistrado) return null;
      derecha = <span className="egr-resultado egr-resultado--ok"><FaCheckCircle aria-hidden="true" /> Desactivado</span>;
    } else if (!accion) {
      derecha = <span className="egr-nota">Se dejó como estaba</span>;
    } else if (!r || r.estado === 'trabajando') {
      derecha = <span className="egr-resultado egr-resultado--trabajando"><FaSpinner className="animate-spin" aria-hidden="true" /> Aplicando</span>;
    } else if (r.estado === 'ok') {
      const Ico = r.alerta ? FaExclamationTriangle : FaCheckCircle;
      derecha = (
        <span className={`egr-resultado ${r.alerta ? 'egr-resultado--alerta' : 'egr-resultado--ok'}`}>
          <Ico aria-hidden="true" /> {r.mensaje}
        </span>
      );
    } else {
      derecha = (
        <span className="egr-resultado egr-resultado--error">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            {r.mensaje}{' '}
            <button type="button" className="vac-link-btn text-xs" onClick={() => cierre.aplicar(s.clave, accion)}>
              Reintentar
            </button>
          </span>
        </span>
      );
    }

    return (
      <li key={s.clave} className="egr-fila">
        <div className="egr-linea">
          <span className="egr-sistema">
            <span className="egr-icono"><Icono aria-hidden="true" /></span>
            <span className="egr-nombre">{s.nombre}</span>
          </span>
          {derecha}
        </div>
        {r?.clave && (
          <div className="egr-clave">
            Clave nueva del correo: <code>{r.clave}</code>
          </div>
        )}
      </li>
    );
  };

  return <ul className="egr-cuentas list-none m-0 p-0">{cierre.sistemas.map(fila)}</ul>;
}

/**
 * Cerrar las cuentas de alguien fuera del egreso: un egresado al que le quedó
 * algo abierto (lo registró GH, o algo falló) o la silla de CIMA de quien
 * cambió de puesto. Muestra lo que sigue abierto y aplica lo que se elija.
 * Sin `empleadoId`, primero se elige al egresado de la lista (no salen en la de empleados).
 */
export default function CierreCuentasModal({ empleadoId = null, tipo = 'egreso', onClose }) {
  const [elegido, setElegido] = useState(empleadoId);
  const [egresados, setEgresados] = useState(null);

  useEffect(() => {
    if (elegido) return;
    axiosInstance.get('/api/onboarding/egresados')
      .then((r) => setEgresados(r.data))
      .catch(() => setEgresados('error'));
  }, [elegido]);

  if (!elegido) {
    return (
      <Modal isOpen onClose={onClose} size="lg">
        <Modal.Header onClose={onClose}>Cerrar cuentas de un egresado</Modal.Header>
        <Modal.Body>
          <p className="egr-ayuda mb-3">Elige a quien ya egresó para ver qué cuentas le quedaron abiertas.</p>
          <select
            className="input"
            value=""
            disabled={!Array.isArray(egresados) || egresados.length === 0}
            onChange={(e) => setElegido(Number(e.target.value))}
          >
            <option value="" disabled>
              {!egresados ? 'Cargando egresados...'
                : egresados === 'error' ? 'No se pudieron cargar los egresados'
                  : egresados.length === 0 ? 'No hay egresados' : 'Elige al egresado'}
            </option>
            {(Array.isArray(egresados) ? egresados : []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}{p.fecha_egreso ? ` (egresó el ${formatFecha(p.fecha_egreso)})` : ''}
              </option>
            ))}
          </select>
        </Modal.Body>
      </Modal>
    );
  }
  return <CierreDe empleadoId={elegido} tipo={tipo} onClose={onClose} />;
}

function CierreDe({ empleadoId, tipo, onClose }) {
  const cierre = useCierreCuentas(empleadoId, { tipo });
  const [nombre, setNombre] = useState('');
  const [fase, setFase] = useState('elegir'); // elegir | aplicando | listo

  useEffect(() => {
    axiosInstance.get(`/api/empleados/${empleadoId}/expediente`)
      .then((r) => setNombre(r.data?.persona?.nombre || ''))
      .catch(() => {});
  }, [empleadoId]);

  const hayQueAplicar = cierre.sistemas.some((s) => {
    const a = cierre.accionDe(s);
    return a && a !== 'dejar';
  });

  const [avisoJefe, setAvisoJefe] = useState(null);

  const aplicar = async () => {
    setFase('aplicando');
    const { paraJefe } = await cierre.aplicarTodo();
    if (tipo === 'egreso') {
      try {
        const r = await axiosInstance.post(`/api/empleados/${empleadoId}/egreso/aviso`, { solo_jefe: true, ...paraJefe });
        if (r.data?.jefe) setAvisoJefe(`Se le avisó a su jefe (${r.data.jefe}).`);
      } catch {
        setAvisoJefe('No se pudo avisar a su jefe.');
      }
    }
    setFase('listo');
  };

  const cerrar = fase === 'aplicando' ? () => {} : onClose;
  const titulo = tipo === 'movimiento' ? 'Su silla en CIMA' : 'Cerrar cuentas';

  return (
    <Modal isOpen onClose={cerrar} size="lg">
      <Modal.Header onClose={cerrar}>{titulo}{nombre ? ` de ${nombre}` : ''}</Modal.Header>
      <Modal.Body>
        {fase === 'elegir' ? (
          <>
            <p className="egr-ayuda mb-3">
              {tipo === 'movimiento'
                ? 'Cambió de puesto: su silla se vacía y eliges quién atiende su cartera hasta que llegue su reemplazo. Su usuario sigue activo.'
                : 'Elige qué pasa con cada cuenta que sigue abierta.'}
            </p>
            <ListaCuentas cierre={cierre} />
          </>
        ) : (
          <>
            {avisoJefe && <p className="egr-ayuda mb-3">{avisoJefe}</p>}
            <ResultadosCuentas cierre={cierre} egresoRegistrado={false} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {fase === 'elegir' ? (
          <>
            <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button
              className="btn btn-danger"
              onClick={aplicar}
              disabled={cierre.consultando || cierre.faltaDestino || !hayQueAplicar}
              title={cierre.consultando ? 'Consultando sus cuentas'
                : cierre.faltaDestino ? 'Elige a quién se redirige su correo'
                  : !hayQueAplicar ? 'No queda nada por cerrar' : undefined}
            >
              {tipo === 'movimiento' ? 'Vaciar silla' : 'Cerrar cuentas'}
            </button>
          </>
        ) : (
          <button className="btn btn-primary" onClick={onClose} disabled={fase === 'aplicando'}>
            {fase === 'aplicando' ? 'Aplicando...' : 'Cerrar'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

const cierrePropType = PropTypes.shape({
  tipo: PropTypes.string,
  sistemas: PropTypes.array,
  cuentas: PropTypes.object,
}).isRequired;

ListaCuentas.propTypes = { cierre: cierrePropType };
ResultadosCuentas.propTypes = { cierre: cierrePropType, egresoRegistrado: PropTypes.bool };
DetalleCima.propTypes = { d: PropTypes.object.isRequired, accion: PropTypes.string, cierre: cierrePropType };
SelectorSuplente.propTypes = {
  id: PropTypes.string.isRequired,
  codigo: PropTypes.string.isRequired,
  candidatos: PropTypes.array,
  cierre: cierrePropType,
};
CierreCuentasModal.propTypes = {
  empleadoId: PropTypes.number,
  tipo: PropTypes.oneOf(['egreso', 'movimiento']),
  onClose: PropTypes.func.isRequired,
};
CierreDe.propTypes = {
  empleadoId: PropTypes.number.isRequired,
  tipo: PropTypes.oneOf(['egreso', 'movimiento']).isRequired,
  onClose: PropTypes.func.isRequired,
};
